import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import CommandCenter from './CommandCenter';
import RemediationDashboard from './RemediationDashboard';
import './Marketplace.css';

const STATUS_COLORS = {
  ACTIVE: { bg: '#d1fae5', text: '#065f46' },
  PENDING: { bg: '#fef3c7', text: '#92400e' },
  PAID: { bg: '#d1fae5', text: '#065f46' },
  VOID: { bg: '#f1f5f9', text: '#64748b' },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f1f5f9', text: '#64748b' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.text }}>
      {status}
    </span>
  );
}

export default function ResellerDashboard() {
  const { t } = useTranslation();
  const [profile, setProfile]       = useState(null);
  const [orgs, setOrgs]             = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [tokens, setTokens]         = useState([]);
  const [tab, setTab]               = useState('overview');
  const [loading, setLoading]       = useState(true);
  const [msg, setMsg]               = useState('');
  const [tokenNotes, setTokenNotes] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get('/partner/reseller/profile'),
      axiosInstance.get('/partner/reseller/organizations'),
      axiosInstance.get('/partner/reseller/commissions'),
      axiosInstance.get('/partner/reseller/tokens'),
    ]).then(([pRes, oRes, cRes, tRes]) => {
      if (pRes.status === 'fulfilled') setProfile(pRes.value.data);
      if (oRes.status === 'fulfilled') setOrgs(oRes.value.data);
      if (cRes.status === 'fulfilled') setCommissions(cRes.value.data);
      if (tRes.status === 'fulfilled') setTokens(tRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGenerateToken = async () => {
    setGenerating(true); setMsg('');
    try {
      await axiosInstance.post('/partner/reseller/tokens', { notes: tokenNotes });
      setTokenNotes('');
      fetchAll();
      setMsg(t('ResellerDashboard.referralLinkGenerated'));
    } catch { setMsg(t('ResellerDashboard.failedToGenerateToken')); }
    finally { setGenerating(false); }
  };

  const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '$0.00';

  if (loading) return <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('ResellerDashboard.loadingResellerPortal')}</div>;

  return (
    <div className="mp-wrap">
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-handshake" /> {t('ResellerDashboard.resellerPortal')}</h2>
          {profile && <p className="mp-subtitle">{t('ResellerDashboard.firmCommissionRate', { firmName: profile.firmName, rate: profile.commissionRate })}</p>}
        </div>
        <div className="mp-status-pill">
          <StatusBadge status={profile?.status || 'ACTIVE'} />
        </div>
      </div>

      {msg && <div className="mp-msg">{msg}</div>}

      {/* KPI cards */}
      {commissions && (
        <div className="mp-kpi-row">
          <div className="mp-kpi">
            <div className="mp-kpi-label">{t('ResellerDashboard.totalEarned')}</div>
            <div className="mp-kpi-value" style={{ color: '#065f46' }}>{fmt(commissions.totalEarned)}</div>
          </div>
          <div className="mp-kpi">
            <div className="mp-kpi-label">{t('ResellerDashboard.pending')}</div>
            <div className="mp-kpi-value" style={{ color: '#92400e' }}>{fmt(commissions.totalPending)}</div>
          </div>
          <div className="mp-kpi">
            <div className="mp-kpi-label">{t('ResellerDashboard.disbursed')}</div>
            <div className="mp-kpi-value">{fmt(commissions.disbursed)}</div>
          </div>
          <div className="mp-kpi">
            <div className="mp-kpi-label">{t('ResellerDashboard.managedOrgs')}</div>
            <div className="mp-kpi-value">{orgs.length}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mp-tabs">
        {[['overview',     'fa-th-large',   t('ResellerDashboard.tabOverview')],
          ['alerts',       'fa-shield-alt', t('ResellerDashboard.tabCommandCenter')],
          ['remediation',  'fa-tasks',      t('ResellerDashboard.tabRemediation')],
          ['orgs',         'fa-building',   t('ResellerDashboard.tabOrganizations', { count: orgs.length })],
          ['commissions',  'fa-coins',      t('ResellerDashboard.tabCommissionLedger')],
          ['tokens',       'fa-link',       t('ResellerDashboard.tabReferralLinks', { count: tokens.length })],
        ].map(([key, icon, label]) => (
          <button key={key} className={`mp-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
            <i className={`fas ${icon}`} /> {label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && profile && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('ResellerDashboard.partnerProfile')}</h3>
          <div className="mp-profile-grid">
            {[[t('ResellerDashboard.firmName'), profile.firmName], [t('ResellerDashboard.contactEmail'), profile.contactEmail],
              [t('ResellerDashboard.website'), profile.websiteUrl], [t('ResellerDashboard.commissionRate'), `${profile.commissionRate}%`],
              [t('ResellerDashboard.stripeAccount'), profile.stripeAccountId || t('ResellerDashboard.notConnected')]].map(([k, v]) => (
              <div key={k} className="mp-profile-row">
                <span className="mp-profile-label">{k}</span>
                <span className="mp-profile-value">{v || '—'}</span>
              </div>
            ))}
          </div>
          <div className="mp-info-box">
            <i className="fas fa-info-circle" /> {t('ResellerDashboard.commissionInfo')}
          </div>
        </div>
      )}

      {/* Command Center */}
      {tab === 'alerts' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('ResellerDashboard.securityCommandCenter')}</h3>
          <p className="mp-section-desc" style={{ marginBottom: 16 }}>
            {t('ResellerDashboard.commandCenterDesc')}
          </p>
          <CommandCenter />
        </div>
      )}

      {/* Remediation */}
      {tab === 'remediation' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('ResellerDashboard.remediationProjects')}</h3>
          <p className="mp-section-desc" style={{ marginBottom: 16 }}>
            {t('ResellerDashboard.remediationDesc')}
          </p>
          <RemediationDashboard />
        </div>
      )}

      {/* Managed Organizations */}
      {tab === 'orgs' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('ResellerDashboard.managedOrganizations')}</h3>
          {orgs.length === 0 ? (
            <div className="mp-empty">{t('ResellerDashboard.noOrgsLinked')}</div>
          ) : (
            <table className="mp-table">
              <thead><tr><th>{t('ResellerDashboard.thOrganization')}</th><th>{t('ResellerDashboard.thContact')}</th><th>{t('ResellerDashboard.thWebsite')}</th><th>{t('ResellerDashboard.thInvoices')}</th></tr></thead>
              <tbody>
                {orgs.map(o => (
                  <tr key={o.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {o.logoUrl
                          ? <img src={o.logoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain' }} />
                          : <div className="mp-org-avatar">{(o.name||'?')[0].toUpperCase()}</div>}
                        <span className="mp-org-name">{o.name}</span>
                      </div>
                    </td>
                    <td>{o.contactEmail || '—'}</td>
                    <td>{o.websiteUrl ? <a href={o.websiteUrl} target="_blank" rel="noreferrer">{o.websiteUrl}</a> : '—'}</td>
                    <td>{o.invoiceCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Commission Ledger */}
      {tab === 'commissions' && commissions && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('ResellerDashboard.commissionLedger')}</h3>
          {commissions.invoices.length === 0 ? (
            <div className="mp-empty">{t('ResellerDashboard.noCommissionRecords')}</div>
          ) : (
            <table className="mp-table">
              <thead><tr><th>{t('ResellerDashboard.thDescription')}</th><th>{t('ResellerDashboard.thServiceType')}</th><th>{t('ResellerDashboard.thAmount')}</th><th>{t('ResellerDashboard.thStatus')}</th><th>{t('ResellerDashboard.thDate')}</th></tr></thead>
              <tbody>
                {commissions.invoices.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.description || '—'}</td>
                    <td>{inv.serviceType}</td>
                    <td style={{ fontWeight: 600 }}>${Number(inv.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td><StatusBadge status={inv.status} /></td>
                    <td>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Referral Tokens */}
      {tab === 'tokens' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('ResellerDashboard.referralLinks')}</h3>
          <div className="mp-token-gen">
            <input className="mp-input" placeholder={t('ResellerDashboard.optionalNotePlaceholder')}
              value={tokenNotes} onChange={e => setTokenNotes(e.target.value)} />
            <button className="mp-btn-primary" onClick={handleGenerateToken} disabled={generating}>
              <i className="fas fa-plus" /> {t('ResellerDashboard.generateLink')}
            </button>
          </div>
          {tokens.length === 0 ? (
            <div className="mp-empty">{t('ResellerDashboard.noReferralLinks')}</div>
          ) : (
            <div className="mp-token-list">
              {tokens.map(tok => (
                <div key={tok.id} className={`mp-token-row ${tok.used ? 'mp-token-used' : ''}`}>
                  <div className="mp-token-info">
                    <div className="mp-token-link">{window.location.origin}/#/register?ref={tok.token}</div>
                    {tok.notes && <div className="mp-token-notes">{tok.notes}</div>}
                    {tok.usedByOrg && <div className="mp-token-used-by"><i className="fas fa-check-circle" /> {t('ResellerDashboard.usedBy', { org: tok.usedByOrg })}</div>}
                    <div className="mp-token-meta">
                      {t('ResellerDashboard.created')} {tok.createdAt ? new Date(tok.createdAt).toLocaleDateString() : ''} ·
                      {t('ResellerDashboard.expires')} {tok.expiresAt ? new Date(tok.expiresAt).toLocaleDateString() : ''}
                      {tok.used && ` · ${t('ResellerDashboard.used')}`}
                    </div>
                  </div>
                  {!tok.used && (
                    <button className="mp-copy-btn" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/#/register?ref=${tok.token}`);
                      setMsg(t('ResellerDashboard.linkCopied'));
                    }}>
                      <i className="fas fa-copy" /> {t('ResellerDashboard.copy')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
